import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Grid } from '@mui/material';
import { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  IOurEntity,
  saveOurEntity,
  createNewBusinessUnitSettings,
  saveActivityLog,
  LogAction,
  LogContextType,
} from 'wisestep-api';
import { ControlledTextInput, WsSelectObj, WsSelect } from 'wisestep-base';
import { string, object } from 'yup';

import DialogWithInput from '../../../components/dialogWithInput';
import {
  getAvEditAddDes,
  activityLogDTOHelper,
} from '../../../features/activityLog/ActivityHelper';
import { failed, success, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { clientSettingsDefaultPayload } from '../../clientsSettings/helper/ClientSettingsHelper';
import { getCountries } from '../helper/CountryHelper';
import { getTimezones } from '../helper/TimezoneHelper';

interface LogoDetail {
  name?: string;
  url?: string;
}

const OurEntityCreate = ({ ourEntityDataObj, setOurEntityId }) => {
  const navigate = useNavigate();
  const [openBUDialog, setOpenBUDialog] = useState<boolean>(false);
  const setNotification = useNotificationStore((state) => state.setNotification);

  const [timezones, setTimezones] = useState<WsSelectObj[]>([]);
  const [logoDetail, setLogoDetail] = useState<LogoDetail>();

  const countries: WsSelectObj[] = getCountries();

  useEffect(() => {
    const timezoneArray: WsSelectObj[] = getTimezones().map((val) => ({
      label: val.label,
      value: val.tzCode,
    }));
    setTimezones(timezoneArray);
  }, []);
  const schema = object().shape({
    Name: string().required('Please provide Entity Name'),
    Registered_address: string().required('Please provide Entity Name'),
    Country: string().required('Please select country'),
  });

  const methods = useForm<IOurEntity>({
    resolver: yupResolver(schema),
    defaultValues: ourEntityDataObj,
  });

  const {
    formState: { dirtyFields },
  } = methods;

  const { handleSubmit, reset } = methods;
  useEffect(() => {
    if (ourEntityDataObj) {
      setLogoDetail({
        url: ourEntityDataObj?.logo,
      });
      reset(ourEntityDataObj);
    }
  }, [ourEntityDataObj, methods, reset]);

  const onSubmit = async (ourEntity: IOurEntity) => {
    ourEntity.logo = logoDetail?.url;
    const response = await saveOurEntity(ourEntity);
    setOurEntityId(response.data.data.id);
    const avPayload = { ...activityLogDTOHelper };
    const avChangedValueViaDirtyFileds = Object.keys(dirtyFields).toString().split(',');
    if (ourEntity.id) {
      avPayload.action = LogAction.Edit;
      const avEmptyEntityfields = Object.keys(
        Object.entries(ourEntityDataObj).reduce((acc, [k, v]) => {
          if (v === '' || v === null) acc[k] = v;
          return acc;
        }, {})
      );
      avPayload.description = `${getAvEditAddDes(
        avEmptyEntityfields,
        avChangedValueViaDirtyFileds
      )} under`;
    } else {
      avPayload.action = LogAction.Add;
      avPayload.description = `ADDED`;
    }

    avPayload.parentEntitiyType = LogContextType.OurEntity;
    avPayload.entityId = response.data.data.id;
    avPayload.parentEntitiyId = response.data.data.id;
    avPayload.field = 'Organisation Entity';
    avPayload.entityName = ourEntity.Name;
    avPayload.contextData = { ourEntityId: response.data.data.id };
    avChangedValueViaDirtyFileds[0].length > 0 ? await saveActivityLog(avPayload) : '';
    setNotification(success('Organisation Entity information saved successfully.'));
    navigate(`/organisationEntity/${response.data.data.id}/details`);
  };

  const onAddBusinessUnit = async (data) => {
    const payload = { ...clientSettingsDefaultPayload };
    payload.name = data.fieldOne;
    try {
      if (payload.name) {
        const response = await createNewBusinessUnitSettings(payload);
        setNotification(success(`${data.fieldOne} has been added successfully !!`));
        setOpenBUDialog(false);
        methods.setValue('Business_unit_id', response.data.id);
      } else setNotification(failed('Please input the name again !!'));
    } catch (error: any) {
      if (error.response.status === 400)
        setNotification(warning(`Oops , ${data.fieldOne} already exists!!`));
      else setNotification(failed('Something went wrong !!'));
    }
  };

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container sx={{ mt: 3 }}>
            <Grid md={6} sx={{ p: 2 }}>
              <ControlledTextInput
                name="Name"
                type="text"
                fullWidth
                placeholder="Organisation Name *"
                variant="outlined"
                labelText="Organisation Name"
              />
            </Grid>
            <Grid md={6} sx={{ p: 2 }}>
              <ControlledTextInput
                name="Legal_Name"
                type="text"
                fullWidth
                placeholder="Legal Name"
                variant="outlined"
                labelText="Legal Name"
              />
            </Grid>
          </Grid>
          <Grid container sx={{ mt: 3 }}>
            <Grid md={12} sx={{ p: 2 }}>
              <ControlledTextInput
                name="Registered_address"
                type="text"
                fullWidth
                multiline
                maxRows={4}
                placeholder="Registered Address *"
                variant="outlined"
                labelText="Registered Address"
              />
            </Grid>
            <Grid md={12} sx={{ p: 2 }}>
              <ControlledTextInput
                name="Corporate_address"
                type="text"
                fullWidth
                multiline
                maxRows={4}
                placeholder="Corporate Address *"
                variant="outlined"
                labelText="Corporate Address"
              />
            </Grid>
          </Grid>

          <Grid container sx={{ mt: 3 }}>
            <Grid md={6} sx={{ p: 2 }}>
              <WsSelect
                name={'Country'}
                defaultValue={ourEntityDataObj?.Country ?? ''}
                label={'Country'}
                selectArray={countries}
              />
            </Grid>
            <Grid md={6} sx={{ p: 2 }}>
              <WsSelect
                name={'Timezone'}
                defaultValue={ourEntityDataObj?.Timezone ?? ''}
                label={'Timezone'}
                selectArray={timezones}
              />
            </Grid>
          </Grid>

          <Grid container justifyContent="flex-end">
            <Grid item sx={{ mr: 2 }}></Grid>
            <Grid item sx={{ mr: 2 }}>
              <Button id="start-button" variant="contained" size="large" type="submit">
                Continue
              </Button>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
      {openBUDialog && (
        <DialogWithInput
          openDialog={openBUDialog}
          closeDialog={() => {
            setOpenBUDialog(false);
            methods.setValue('Business_unit_id', undefined);
          }}
          onInput={(data) => onAddBusinessUnit(data)}
          name="Business Unit"
          gridXs={12}
        />
      )}
    </>
  );
};

export default OurEntityCreate;
